import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shuffle, RotateCcw, ArrowRight, Loader2, Quote } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { ACCENT_FLAGS } from '../lib/audio';
import { tone } from '../lib/score';
import { useVoiceContour } from '../lib/voiceContour';
import { useRecorder } from '../hooks/useRecorder';
import RecordStage from '../components/brand/RecordStage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MIN_SECONDS = 10;
const TARGET_SECONDS = 25;
const MAX_SECONDS = 60;

const TOPICS = [
  'Describe your typical morning routine',
  'Talk about a movie or book you love, and why',
  'Describe the city or town where you grew up',
  'Explain what you do for work or study',
  'Tell me about the best trip you’ve taken',
  'What would your perfect weekend look like?',
  'Describe a meal you could eat every day',
  'Talk about a skill you’d like to learn',
];

const LISTENING_LINES = [
  'Listening to your vowels…',
  'Comparing your rhythm with other accents…',
  'Checking how you stress syllables…',
  'Looking at your consonants…',
  'Almost there…',
];

const Bar = ({ value }) => (
  <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
    <div className={`h-full rounded-full bg-gradient-to-r ${tone(value).bar}`} style={{ width: `${value}%`, transition: 'width 1s ease-out' }} />
  </div>
);

const Result = ({ result, onAgain }) => {
  const accent = result.accent_detected;
  const conf = Math.round((accent.confidence || 0) * 100);
  const matches = accent.all_confidences
    ? Object.entries(accent.all_confidences).sort((a, b) => b[1] - a[1]).slice(0, 4)
    : [];

  return (
    <div className="mx-auto max-w-4xl px-5 pt-10 sm:pt-14" data-testid="accent-test-results-page">
      <section className="text-center animate-fade-up">
        <div className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-white/[0.04] text-6xl ring-1 ring-white/10" data-testid="accent-flag">
          {ACCENT_FLAGS[accent.accent] || '🌍'}
        </div>
        <p className="cz-eyebrow mt-6">My guess</p>
        <h1 className="mt-2 text-5xl font-semibold tracking-[-0.03em] text-white sm:text-7xl" data-testid="accent-results-heading">
          <span className="cz-display" data-testid="accent-name">{accent.accent}</span>
        </h1>
        <div className="mx-auto mt-5 max-w-xs">
          <div className="mb-2 flex justify-between text-xs text-white/50">
            <span>Confidence</span><span className="font-semibold text-white">{conf}%</span>
          </div>
          <Bar value={conf} />
        </div>
      </section>

      <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
        {accent.markers?.length > 0 && (
          <div className="cz-card p-6" data-testid="accent-markers">
            <h2 className="font-semibold text-white">What gave it away</h2>
            <ul className="mt-4 space-y-3">
              {accent.markers.map((m, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed text-white/65">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-coral-500" />{m}
                </li>
              ))}
            </ul>
          </div>
        )}
        {matches.length > 0 && (
          <div className="cz-card p-6" data-testid="accent-top-matches">
            <h2 className="font-semibold text-white">Closest matches</h2>
            <div className="mt-4 space-y-4">
              {matches.map(([name, c]) => (
                <div key={name}>
                  <div className="mb-1.5 flex justify-between text-sm">
                    <span className="text-white/75">{ACCENT_FLAGS[name] || '🌍'} {name}</span>
                    <span className="font-semibold tabular-nums text-white">{Math.round(c * 100)}%</span>
                  </div>
                  <Bar value={c * 100} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {accent.explanation && (
        <div className="cz-card mt-4 p-6" data-testid="accent-explanation">
          <h2 className="font-semibold text-white">How I could tell</h2>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-white/65">{accent.explanation}</p>
        </div>
      )}

      {accent.accent_tips?.length > 0 && (
        <div className="cz-card mt-4 border-brand-400/20 bg-brand-500/[0.06] p-6" data-testid="accent-tips">
          <h2 className="font-semibold text-white">Where to start</h2>
          <ol className="mt-4 space-y-3">
            {accent.accent_tips.map((tip, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-white/75">
                <span className="font-mono text-xs text-brand-300">0{i + 1}</span>{tip}
              </li>
            ))}
          </ol>
        </div>
      )}

      {result.transcript && (
        <div className="cz-card mt-4 p-6" data-testid="transcript-card">
          <Quote className="h-5 w-5 text-white/25" />
          <p className="mt-2 text-lg leading-relaxed text-white/85" data-testid="transcript-text">{result.transcript}</p>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="action-buttons">
        <button type="button" onClick={onAgain} className="cz-btn-ghost sm:flex-1" data-testid="test-again-btn">
          <RotateCcw className="h-4 w-4" /> Test again
        </button>
        <Link to="/practice" className="cz-btn-primary sm:flex-1" data-testid="go-practice-btn">
          Work on these sounds <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

const AccentTest = () => {
  const [topic, setTopic] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lineIdx, setLineIdx] = useState(0);
  const [result, setResult] = useState(null);
  const contour = useVoiceContour();
  const recorder = useRecorder({ contour, maxSeconds: MAX_SECONDS });
  const { status, seconds } = recorder;

  useEffect(() => {
    if (!isAnalyzing) return undefined;
    setLineIdx(0);
    const id = setInterval(() => setLineIdx((i) => (i + 1) % LISTENING_LINES.length), 2000);
    return () => clearInterval(id);
  }, [isAnalyzing]);

  const analyzeAccent = async () => {
    if (!recorder.blob || seconds < MIN_SECONDS) return;
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('audio', recorder.blob, 'accent-test.wav');
      const response = await axios.post(`${API}/detect-accent`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error detecting accent:', error);
      const detail = error.response?.data?.detail;
      toast({
        title: 'Analysis failed',
        description: (typeof detail === 'object' ? detail.reason || detail.error : detail) || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const again = () => {
    setResult(null);
    recorder.reset();
    window.scrollTo(0, 0);
  };

  if (result?.accent_detected) return <Result result={result} onAgain={again} />;

  const tooShort = status === 'ready' && seconds < MIN_SECONDS;
  const hint = status === 'recording'
    ? seconds < MIN_SECONDS ? 'Keep going…' : seconds < TARGET_SECONDS ? 'Good — a little more makes the guess sharper' : 'Perfect. Stop whenever you’re ready'
    : status === 'processing' ? 'Preparing your take…'
    : tooShort ? `Too short — I need at least ${MIN_SECONDS} seconds`
    : status === 'ready' ? `${seconds} seconds — enough for a good guess`
    : `Tap the mic and talk for about ${TARGET_SECONDS} seconds`;

  return (
    <div className="mx-auto max-w-3xl px-5 pt-8 sm:pt-12">
      <div className="text-center animate-fade-up">
        <p className="cz-eyebrow">Accent test</p>
        <h1 className="mt-3 text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-white sm:text-6xl">
          I bet I can guess your accent{' '}
          <span className="cz-display text-transparent bg-clip-text bg-gradient-to-r from-coral-400 to-coral-300">in 30 seconds.</span>
        </h1>
        <p className="mx-auto mt-4 max-w-md text-white/55">Talk naturally — don’t read. Here’s something to talk about:</p>
      </div>

      <div className="cz-card mt-8 flex items-center gap-4 p-5 animate-fade-up [animation-delay:100ms]">
        <p className="flex-1 text-lg font-medium leading-snug text-white sm:text-xl" key={topic}>
          <span className="animate-fade-up inline-block">“{TOPICS[topic]}”</span>
        </p>
        <button
          type="button"
          onClick={() => setTopic((t) => (t + 1) % TOPICS.length)}
          disabled={status === 'recording'}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/10 text-white/70 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-30"
          aria-label="Another topic"
        >
          <Shuffle className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4">
        {isAnalyzing ? (
          <div className="cz-card grid min-h-[320px] place-items-center p-8 text-center">
            <div>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-coral-400" />
              <p className="mt-5 text-lg font-medium text-white animate-fade-up" key={lineIdx} aria-live="polite">{LISTENING_LINES[lineIdx]}</p>
              <p className="mt-2 text-sm text-white/40">This takes about 10–20 seconds.</p>
            </div>
          </div>
        ) : (
          <RecordStage contour={contour} recorder={recorder} targetSeconds={TARGET_SECONDS} hint={hint} />
        )}
      </div>

      {status === 'ready' && !isAnalyzing && (
        <div className="mt-4 flex gap-3 animate-fade-up">
          <button type="button" onClick={recorder.reset} className="cz-btn-ghost flex-1">
            <RotateCcw className="h-4 w-4" /> Redo
          </button>
          <button type="button" onClick={analyzeAccent} disabled={tooShort} className="cz-btn-primary flex-[2]" data-testid="detect-accent-btn">
            Reveal my accent <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AccentTest;
